import React, { useState } from 'react';
import { Award, Loader2, TrendingUp, AlertCircle, CheckCircle, Clock, ClipboardList } from 'lucide-react';
import { useMyGrades } from '../../hooks/useStudent';
import { getStatusColor } from '@/types/student.types';
import SurveyModal from '../survey/SurveyModal';
import { useQueryClient } from '@tanstack/react-query';
import { studentKeys } from '../../hooks/useStudent';

interface GradesTabProps {
  moduleElementId: string;
  // Past year = read-only consultation; absent = current year
  academicYearId?: string;
  // Former group's course instance; absent = the current group
  groupeId?: string;
}

const GradesTab: React.FC<GradesTabProps> = ({ moduleElementId, academicYearId, groupeId }) => {
  const { data: grades, isLoading, error } = useMyGrades(moduleElementId, academicYearId, groupeId);
  const [showSurvey, setShowSurvey] = useState(false);
  const queryClient = useQueryClient();

  const handleSurveyComplete = () => {
    setShowSurvey(false);
    // Refresh grades data after survey completion
    queryClient.invalidateQueries({ queryKey: studentKeys.grades(moduleElementId, academicYearId) });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Erreur lors du chargement des notes
      </div>
    );
  }

  if (!grades || grades.components.length === 0) {
    return (
      <div className="text-center py-12">
        <Award className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Notes non disponibles</h3>
        <p className="mt-2 text-sm text-gray-500">
          La configuration des notes n'a pas encore été définie pour ce cours.
        </p>
      </div>
    );
  }

  // Check if survey is required but not completed
  if (grades.surveyRequired && !grades.surveyCompleted) {
    return (
      <div className="space-y-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Award className="h-5 w-5 text-blue-600" />
          Mes Notes
        </h2>

        {/* Survey Required Banner */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <ClipboardList className="h-8 w-8 text-amber-600" />
            </div>
            <h3 className="text-xl font-semibold text-amber-900 mb-2">
              Évaluation du cours requise
            </h3>
            <p className="text-amber-700 mb-4 max-w-md">
              Pour consulter vos notes, vous devez d'abord compléter l'évaluation anonyme de ce cours.
              Vos réponses nous aident à améliorer la qualité de l'enseignement.
            </p>
            <p className="text-sm text-amber-600 mb-4">
              Notes entrées : {grades.cumulativeGradeWeight}% du total
            </p>
            <button
              onClick={() => setShowSurvey(true)}
              className="px-6 py-3 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition-colors flex items-center gap-2"
            >
              <ClipboardList className="h-5 w-5" />
              Répondre au sondage
            </button>
          </div>
        </div>

        {/* Survey Modal */}
        {showSurvey && grades.groupeId && grades.academicYearId && (
          <SurveyModal
            moduleElementId={moduleElementId}
            groupeId={grades.groupeId}
            academicYearId={grades.academicYearId}
            onClose={() => setShowSurvey(false)}
            onComplete={handleSurveyComplete}
          />
        )}
      </div>
    );
  }

  // Calculate progress
  const completedComponents = grades.components.filter((c) => c.score !== null).length;
  const totalComponents = grades.components.length;
  const progressPercent = Math.round((completedComponents / totalComponents) * 100);

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'VALIDATED':
        return 'Validé';
      case 'FAILED':
        return 'Non validé';
      case 'RATTRAPAGE':
        return 'Rattrapage';
      case 'INCOMPLETE':
      default:
        return 'En cours';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'VALIDATED':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'FAILED':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'RATTRAPAGE':
        return <AlertCircle className="h-5 w-5 text-amber-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  // A student who changed group mid-year sees the components of both groups
  const showGroups = new Set((grades?.components ?? []).map((c) => c.groupName).filter(Boolean)).size > 1;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <Award className="h-5 w-5 text-blue-600" />
        Mes Notes
      </h2>

      {/* Summary Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Average */}
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">
              {grades.averageGrade !== null ? grades.averageGrade.toFixed(2) : '--'}
              <span className="text-lg text-gray-500">/20</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Moyenne actuelle</p>
          </div>

          {/* Progress */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2">
              <TrendingUp className="h-6 w-6 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">{progressPercent}%</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {completedComponents}/{totalComponents} composantes
            </p>
            {/* Progress bar */}
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Status */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2">
              {getStatusIcon(grades.status)}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(grades.status)}`}>
                {getStatusLabel(grades.status)}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-2">Statut du module</p>
          </div>
        </div>
      </div>

      {/* Grade Components Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Composante
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Coefficient
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Note Max
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ma Note
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {grades.components.map((component) => (
              <tr key={component.code} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{component.name}</div>
                  {showGroups && component.groupName && (
                    <div className="text-xs text-gray-500 mt-0.5">{component.groupName}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <span className="text-gray-900">{component.percentage}%</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <span className="text-gray-900">{component.maxScore}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  {component.score !== null ? (
                    <span className={`font-semibold ${
                      component.score >= component.maxScore * 0.5 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {component.score}/{component.maxScore}
                    </span>
                  ) : (
                    <span className="text-gray-400">--</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  {component.score !== null ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="h-3.5 w-3.5 mr-1" />
                      Noté
                    </span>
                  ) : component.absenceStatus !== 'NONE' ? (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      component.absenceStatus === 'ABSENT_EXCUSED'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {component.absenceStatus === 'ABSENT_EXCUSED' ? 'Absence justifiée' : 'Absent'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      <Clock className="h-3.5 w-3.5 mr-1" />
                      En attente
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Note about incomplete grades */}
      {!grades.isComplete && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg text-sm">
          <p>
            <strong>Note :</strong> La moyenne affichée est calculée sur la base des notes déjà saisies.
            Elle sera mise à jour lorsque toutes les composantes seront notées.
          </p>
        </div>
      )}
    </div>
  );
};

export default GradesTab;
